import { useEffect, useState } from 'react';
import { ArrowLeft, Speech, Volume2, VolumeX } from 'lucide-react';
import { Stage, readAloud } from '@gmp/shared-ui';
import { navigate } from '../app/router';
import { setPrefs, sound, usePrefs } from '../app/prefs';
import { usePartyState } from '../App';
import { party } from '../net/client';
import { ProfileFields } from './Profile';
import { TurnAlerts } from '../app/TurnAlerts';
import { SiteFooter } from '../app/Footer';
import { AccountPanel } from './Account';

export function SettingsPanel() {
  const prefs = usePrefs();
  const { room } = usePartyState();
  return (
    <div className="grid gap-5">
      <ProfileFields
        nickname={prefs.nickname}
        avatar={prefs.avatar}
        onNickname={(v) => setPrefs({ nickname: v })}
        onAvatar={(a) => {
          setPrefs({ avatar: a });
          if (room) party.profile(undefined, a);
        }}
      />
      {room && prefs.nickname.trim() && (
        <button className="btn btn-secondary btn-sm justify-self-start" onClick={() => party.profile(prefs.nickname.trim())}>Update my name in this party</button>
      )}
      <div className="flex items-center gap-3">
        <button className="btn btn-sm btn-secondary" aria-pressed={!prefs.muted} onClick={() => { setPrefs({ muted: !prefs.muted }); sound.unlock(); sound.play('select'); }}>
          {prefs.muted ? <VolumeX size={18} /> : <Volume2 size={18} />} {prefs.muted ? 'Sound effects off' : 'Sound effects on'}
        </button>
        <label className="flex-1 flex items-center gap-2 text-sm">
          Volume
          <input type="range" aria-label="Sound effects volume" min={0} max={1} step={0.1} value={prefs.volume} onChange={(e) => setPrefs({ volume: Number(e.target.value) })} onPointerUp={() => sound.play('tap')} className="flex-1 accent-[var(--accent)]" />
        </label>
      </div>
      <label className="flex items-center gap-3">
        <input type="checkbox" className="size-5 accent-[var(--accent)]" checked={prefs.reducedMotion} onChange={(e) => setPrefs({ reducedMotion: e.target.checked })} />
        Reduce motion (fewer animations)
      </label>
      <ReadAloudSettings />
      <TurnAlerts />
    </div>
  );
}

const TEST_LINE = "Hi! This is how I'll read the questions. Here's the quote: I'll be back. Which movie is it? A: The Terminator. B: Toy Story.";

/** Accessibility: read prompts and choices aloud on this device (hidden where the browser has no speech). */
function ReadAloudSettings() {
  const prefs = usePrefs();
  const [, setTick] = useState(0);
  // Some browsers load their voices a moment after the page: refresh the picker when they arrive.
  useEffect(() => readAloud.subscribe(() => setTick((t) => t + 1)), []);
  if (!readAloud.supported) return null;
  const voices = readAloud.voices();
  const current = readAloud.currentVoice();
  const test = () => {
    readAloud.unlock();
    readAloud.speak(TEST_LINE, { force: true });
  };
  return (
    <div className="grid gap-3" data-testid="read-aloud-settings">
      <label className="flex items-center gap-3">
        <input
          type="checkbox"
          className="size-5 accent-[var(--accent)]"
          checked={prefs.readAloud}
          data-testid="read-aloud-toggle"
          onChange={(e) => {
            setPrefs({ readAloud: e.target.checked });
            // This tap counts as the gesture iOS needs before a page may speak.
            if (e.target.checked) readAloud.unlock();
          }}
        />
        <span className="flex items-center gap-2"><Speech size={18} aria-hidden /> Read aloud (accessibility)</span>
      </label>
      <p className="text-sm text-mute -mt-2">Reads each question and its choices out loud on this phone (never on the TV).</p>
      {prefs.readAloud && (
        <div className="grid gap-3 pl-8">
          {voices.length > 0 && (
            <label className="grid gap-1 text-sm">
              <span className="font-bold">Voice</span>
              <select
                className="rounded-xl bg-ink border border-line px-3 py-2"
                value={prefs.voiceURI && voices.some((v) => v.voiceURI === prefs.voiceURI) ? prefs.voiceURI : ''}
                onChange={(e) => setPrefs({ voiceURI: e.target.value || null })}
                data-testid="read-aloud-voice"
              >
                <option value="">Automatic{current ? ` (${current.name})` : ''}</option>
                {voices.map((v) => (
                  <option key={v.voiceURI} value={v.voiceURI}>{v.name} · {v.lang}</option>
                ))}
              </select>
            </label>
          )}
          <label className="flex items-center gap-2 text-sm">
            <span className="font-bold">Speed</span>
            <input type="range" aria-label="Read-aloud speed" min={0.8} max={1.2} step={0.1} value={prefs.speechRate} onChange={(e) => setPrefs({ speechRate: Number(e.target.value) })} className="flex-1 accent-[var(--accent)]" data-testid="read-aloud-rate" />
            <span className="tabular-nums w-10 text-right">{prefs.speechRate.toFixed(1)}×</span>
          </label>
          <button className="btn btn-sm btn-secondary justify-self-start" onClick={test} data-testid="read-aloud-test"><Volume2 size={18} /> Test voice</button>
        </div>
      )}
    </div>
  );
}

export function Settings() {
  return (
    <Stage>
      <main className="flex-1 flex flex-col gap-6 safe-pad safe-top safe-bottom py-6 max-w-lg w-full mx-auto">
        <header className="flex items-center gap-3">
          <button className="btn btn-sm btn-secondary" onClick={() => navigate({ name: 'home' })} aria-label="Back"><ArrowLeft size={18} /></button>
          <h1 className="font-display font-black text-3xl">Settings</h1>
        </header>
        <section className="card p-5"><SettingsPanel /></section>
        <AccountPanel />
        <p className="text-xs text-mute">Accounts are optional, no ads, no third-party tracking. Photos and videos are only shared with your party and deleted automatically.</p>
        <SiteFooter className="mt-auto" />
      </main>
    </Stage>
  );
}
